import { z } from 'zod';
import { kePhoneSchema, kraPinSchema } from './common.schemas';
import { defineListQuery } from './list-query.schemas';

/** An owner's payout method (PRD → Owner reporting → Payouts and defaults): exactly one branch, never both. */
export const PAYOUT_METHODS = ['mpesa', 'bank'] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

/** What an owner can own (gitaumoses4/chukua#15) — a truck and a trailer share the same ownership model, one code path. */
export const OWNABLE_ASSET_TYPES = ['truck', 'trailer'] as const;
export type OwnableAssetType = (typeof OWNABLE_ASSET_TYPES)[number];

export const payoutDetailsSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('mpesa'), mpesaNumber: kePhoneSchema }),
  z.object({
    method: z.literal('bank'),
    bankName: z.string().trim().min(1).max(120),
    bankAccountName: z.string().trim().min(1).max(120),
    bankAccountNumber: z.string().trim().min(1).max(64),
  }),
]);
export type PayoutDetails = z.infer<typeof payoutDetailsSchema>;

/** `POST /owners` and `PATCH /owners/:id` — owners aren't activated/deactivated, so there is no status route (gitaumoses4/chukua#15). */
export const createOwnerSchema = z.object({
  name: z.string().trim().min(1).max(160),
  phone: kePhoneSchema,
  email: z.email(),
  kraPin: kraPinSchema,
  payoutDetails: payoutDetailsSchema,
});
export type CreateOwnerInput = z.infer<typeof createOwnerSchema>;

export const updateOwnerSchema = createOwnerSchema;
export type UpdateOwnerInput = z.infer<typeof updateOwnerSchema>;

/** `GET /owners` — search matches name; no status filter, owners aren't activated/deactivated. */
export const ownerListQuerySchema = defineListQuery({
  sortFields: ['name', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
});
export type OwnerListQuery = z.infer<typeof ownerListQuerySchema>;

/** `POST /trucks/:truckId/ownership-periods/transfer` or `POST /trailers/:trailerId/ownership-periods/transfer` — closes the running period and opens the next one. */
export const transferAssetOwnershipSchema = z.object({
  ownerId: z.string().uuid(),
  effectiveFrom: z.iso.date(),
});
export type TransferAssetOwnershipInput = z.infer<typeof transferAssetOwnershipSchema>;
